import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, MessageCircle, CheckSquare, Briefcase, AlertTriangle, Zap, Megaphone, Clock, Check, Trash2, CheckCheck, Filter, ClipboardList } from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  whatsapp:          { icon: MessageCircle, color: "#10b981", label: "WhatsApp" },
  form_lead:         { icon: ClipboardList, color: "#6366f1", label: "Formulário" },
  tarefa_atribuida:  { icon: CheckSquare,   color: "#3b82f6", label: "Tarefa" },
  tarefa_vencendo:   { icon: Clock,         color: "#f59e0b", label: "Tarefa" },
  negocio_atribuido: { icon: Briefcase,     color: "#6366f1", label: "Negócio" },
  sla_breach:        { icon: AlertTriangle, color: "#ef4444", label: "SLA" },
  sistema:           { icon: Zap,           color: "#94a3b8", label: "Sistema" },
  broadcast:         { icon: Megaphone,     color: "#f97316", label: "Aviso" },
};

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `há ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `há ${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function NotificacoesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterType, setFilterType] = useState("");
  const [filterRead, setFilterRead] = useState("todas");
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3b82f6";
  const navigate = useNavigate();

  const fetchNotifs = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 25 });
      if (filterType) params.set("notif_type", filterType);
      if (filterRead === "nao_lidas") params.set("unread_only", "true");
      const res = await axios.get(`${API}/notifications?${params}`, { withCredentials: true });
      setItems(res.data.items);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
      setPage(p);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [filterType, filterRead]);

  useEffect(() => { fetchNotifs(1); }, [fetchNotifs]);

  useEffect(() => {
    const onSSE = () => fetchNotifs(1);
    window.addEventListener("pipe4:notification", onSSE);
    return () => window.removeEventListener("pipe4:notification", onSSE);
  }, [fetchNotifs]);

  const markRead = async (id) => {
    await axios.patch(`${API}/notifications/${id}/read`, {}, { withCredentials: true });
    setItems(prev => prev.map(n => n.id === id ? { ...n, lida: true } : n));
  };

  const markUnread = async (id) => {
    await axios.patch(`${API}/notifications/${id}/unread`, {}, { withCredentials: true });
    setItems(prev => prev.map(n => n.id === id ? { ...n, lida: false } : n));
  };

  const deleteNotif = async (id) => {
    await axios.delete(`${API}/notifications/${id}`, { withCredentials: true });
    setItems(prev => prev.filter(n => n.id !== id));
    setTotal(prev => prev - 1);
  };

  const markAllRead = async () => {
    await axios.post(`${API}/notifications/read-all`, {}, { withCredentials: true });
    setItems(prev => prev.map(n => ({ ...n, lida: true })));
  };

  const handleClick = async (notif) => {
    if (!notif.lida) await markRead(notif.id);
    if (notif.link) navigate(notif.link);
  };

  const unreadCount = items.filter(n => !n.lida).length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${tc500}18` }}>
          <Bell className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight">Notificações</h1>
          <p className="text-slate-500 mt-0.5 text-sm">{total} no total · {unreadCount} não lidas</p>
        </div>
      </div>

      {/* Filters + actions */}
      <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-3">
        <Filter className="w-4 h-4 text-slate-400 flex-shrink-0" />

        <Select value={filterRead} onValueChange={v => { setFilterRead(v); }}>
          <SelectTrigger className="w-36 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas</SelectItem>
            <SelectItem value="nao_lidas">Não lidas</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterType || "all"} onValueChange={v => setFilterType(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40 h-8 text-xs">
            <SelectValue placeholder="Todos os tipos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
              <SelectItem key={key} value={key}>{cfg.label} — {key.replace(/_/g, " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-2">
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={markAllRead}>
              <CheckCheck className="w-3.5 h-3.5" />Marcar tudo como lido
            </Button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading && (
          <div className="flex items-center justify-center py-12 text-slate-400 text-sm">
            <div className="animate-spin w-5 h-5 border-2 rounded-full mr-2" style={{ borderColor: `${tc500}40`, borderTopColor: tc500 }} />
            Carregando...
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Bell className="w-10 h-10 mb-3 text-slate-200" />
            <p className="text-sm font-medium text-slate-500">Nenhuma notificação encontrada</p>
            <p className="text-xs mt-1">Ajuste os filtros ou aguarde novas notificações</p>
          </div>
        )}

        {items.map((notif, i) => {
          const cfg = TYPE_CONFIG[notif.type] || TYPE_CONFIG.sistema;
          const Icon = cfg.icon;
          return (
            <div key={notif.id}
              className={`flex items-start gap-4 px-5 py-4 border-b border-slate-100 last:border-0 cursor-pointer group transition-colors ${notif.lida ? "hover:bg-slate-50" : "bg-blue-50/30 hover:bg-blue-50/60"}`}
              onClick={() => handleClick(notif)}>

              {/* Unread dot */}
              <div className="w-2 flex-shrink-0 mt-2">
                {!notif.lida && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: tc500 }} />}
              </div>

              {/* Icon */}
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${cfg.color}18` }}>
                <Icon className="w-4 h-4" style={{ color: cfg.color }} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className={`text-sm leading-snug ${notif.lida ? "text-slate-600" : "font-semibold text-slate-800"}`}>
                    {notif.titulo}
                  </p>
                  <span className="text-[11px] text-slate-400 flex-shrink-0 mt-0.5">{timeAgo(notif.created_at)}</span>
                </div>
                {notif.mensagem && (
                  <p className="text-xs text-slate-500 mt-0.5">{notif.mensagem}</p>
                )}
                <span className="inline-block text-[10px] px-1.5 py-0.5 rounded-full font-medium mt-1.5"
                  style={{ backgroundColor: `${cfg.color}15`, color: cfg.color }}>
                  {cfg.label}
                </span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                {notif.lida
                  ? <button onClick={e => { e.stopPropagation(); markUnread(notif.id); }} title="Marcar como não lida"
                      className="p-1.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors">
                      <Bell className="w-3.5 h-3.5" />
                    </button>
                  : <button onClick={e => { e.stopPropagation(); markRead(notif.id); }} title="Marcar como lida"
                      className="p-1.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors">
                      <Check className="w-3.5 h-3.5" />
                    </button>
                }
                <button onClick={e => { e.stopPropagation(); deleteNotif(notif.id); }} title="Excluir"
                  className="p-1.5 rounded hover:bg-rose-100 text-slate-400 hover:text-rose-500 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => fetchNotifs(page - 1)}>Anterior</Button>
          <span className="text-sm text-slate-500">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => fetchNotifs(page + 1)}>Próxima</Button>
        </div>
      )}
    </div>
  );
}
